/* R796 Battlegrounds page: boot (contract §7.4), in the approved look.
   The boot panel is already painted by the section markup and the layout's
   skeleton CSS, so the first frame never waits on this file. Then:
     signed out, or home says has_hero false  "Bring your hero to life", which
                                              sends the player to Add a Hero in
                                              the Hall (R800: signed out, with the
                                              guest Prologue on, also offers three
                                              borrowed heroes for the Prologue)
     otherwise                                catalog() and home() in parallel,
                                              bounded at 6 s (an error state with
                                              Try again), then the game shell
   Home is drawn only once the two faces (EB Garamond, Geist) are in, bounded at
   700 ms, so a font swap never moves the layout. After the first paint
   (requestAnimationFrame, then requestIdleCallback with a 2 s bound) it
   preloads the effects libraries and warms the other screens' modules.
   Offline shows a banner and disables FIGHT; coming back online reloads home
   once. It registers nothing on the Hall.
   ctx (below) is the one object every screen gets: the store, the API client,
   the router and the few actions screens share (fight, open a pack, show a
   result). Screens never compute gold, stars, odds or rolls: they paint the
   last server reply. */

import { createApi, errorKind, playerLine } from 'bg-data';
import { createStore, patchSlice } from 'bg-state';
import { createRouter } from 'bg-router';
/* No vendor preload (F1, 2026-09-27): PIXI and GSAP (about 1.13 MB) are fetched only when a module calls
   bg-vendor's load(). No module draws with them yet, so a phone no longer downloads them after the first screen. */
import { applyTokens } from 'bg-tokens';
import { h, icon, portrait, sheet, toast, clear, px, motionPref, firstName } from 'bg-ui';
import { chapterOf, openerDue } from 'bg-story';
import { createShell } from 'bg-shell';
import { posterSrc } from 'bg-home';

const BOOT_BOUND_MS = 6000;
const FONT_BOUND_MS = 700;
const LAZY = ['bg-heroes', 'bg-hero', 'bg-shop', 'bg-levels', 'bg-upgrades', 'bg-events', 'bg-pack', 'bg-result', 'bg-champion', 'bg-cutscene', 'bg-lore'];
/* R800: the first visit's title and intro (bg-cutscene.js keeps the same key), and the bosses
   whose intro card has played on this device ('*' is every boss) */
const INTRO_KEY = 'bgw-intro-v1';
const BOSS_SEEN_KEY = 'bgw-bosscard-v1';
function introSeen() { try { return localStorage.getItem(INTRO_KEY) === '1'; } catch (_) { return true; } }
function bossSeen(id) { try { const v = localStorage.getItem(BOSS_SEEN_KEY) || ''; return v === '*' || v.split(',').indexOf(id) >= 0; } catch (_) { return true; } }
function markBossSeen(id) { try { const v = localStorage.getItem(BOSS_SEEN_KEY) || ''; if (v !== '*' && v.split(',').indexOf(id) < 0) localStorage.setItem(BOSS_SEEN_KEY, (v ? v + ',' : '') + id); } catch (_) {} }
const FACES_KEY = 'bgw-pack-faces-v1';

/* the pack faces: picked once per visit from the Hall's public roster, kept for the session */
function readFaces() {
  try {
    const v = JSON.parse(sessionStorage.getItem(FACES_KEY) || 'null');
    return v && v.v === 1 && Array.isArray(v.mythic) && Array.isArray(v.common) ? v : null;
  } catch (_) { return null; }
}
function pickFew(list, n) {
  const a = (list || []).slice();
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); const t = a[i]; a[i] = a[j]; a[j] = t; }
  return a.slice(0, n).map(x => ({ character_id: x.character_id, name: x.name, thumb: x.thumb || x.img, frame: x.frame }));
}

function bound(promise, ms) {
  let timer = null;
  const late = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const e = new Error('The Battlegrounds took too long to answer. Try again.');
      e.__hohTimeout = true;
      reject(e);
    }, ms);
  });
  return Promise.race([promise, late]).finally(() => clearTimeout(timer));
}
const sleep = ms => new Promise(r => setTimeout(r, ms));
/* 2026-09-28 (soak S1): a second tap on Fight or the Prologue while the first one's boss card is still coming started the
   level twice and mounted the battle over the running fight; one entry at a time. The first visit's title waits for the
   Hub, so a tap in between never lands on a Hub the title is about to cover */
let fightGate = false, titleUp = null;

function afterFirstPaint(fn) {
  const run = () => { try { fn(); } catch (_) {} };
  requestAnimationFrame(() => {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 2000);
  });
}
/* the first screen's own bytes first: its images and faces, bounded at 4 s,
   so the pack faces and the lazy screens never compete with the largest paint */
function afterFirstScreen(root, fn) {
  const imgs = Array.from(root.querySelectorAll('img')).filter(i => !i.complete);
  const done = Promise.all(imgs.map(i => new Promise(r => { i.addEventListener('load', r, { once: true }); i.addEventListener('error', r, { once: true }); })));
  const fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
  Promise.race([Promise.all([done, fonts]), sleep(4000)]).then(() => afterFirstPaint(fn));
}

/* the two faces of the look; resolves early when they are already in */
function fontsReady() {
  if (!document.fonts || typeof document.fonts.load !== 'function') return Promise.resolve();
  const link = document.getElementById('bg-fonts');
  const sheetIn = new Promise(resolve => {
    if (!link || link.rel === 'stylesheet' || link.sheet) { resolve(); return; }
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
  });
  const faces = sheetIn.then(() => Promise.all([
    document.fonts.load("500 32px 'EB Garamond'"), document.fonts.load("400 15px 'Geist'"), document.fonts.load("500 15px 'Geist'"), document.fonts.load("600 15px 'Geist'")
  ])).catch(() => null);
  return Promise.race([faces, sleep(FONT_BOUND_MS)]);
}

function boot() {
  const root = document.getElementById('bg-app');
  if (!root || root.hasAttribute('data-bg-booted')) return;
  root.setAttribute('data-bg-booted', '');
  const cfg = window.HOH_BG_CONFIG || {};
  const HALL = cfg.hallUrl || '/pages/hall-of-heroes';
  applyTokens(document.documentElement);

  if (motionPref() === 'reduce') document.documentElement.classList.add('bg-rm');
  const store = createStore();
  const api = createApi(Object.assign({}, cfg, { onPending: n => patchSlice(store, 'net', { pending: n }) }));
  let router = null, shell = null, loadSeq = 0, vendorAsked = false, cardsSeq = 0;
  const faces = { value: null, loading: null, failed: false };
  const bootPanel = root.querySelector('[data-bg-boot]');
  const bootLine = root.querySelector('[data-bg-boot-line]');
  const fontsGate = fontsReady();

  /* ---------------- R801: a boss's own face when the server names no poster for it ----------------
     The catalog boss's poster_character_id (else its level's final row face: a Hall id, or {character_id, image:
     'artwork', artwork_id}), read once through the Hall's public columns (bg-data hallFaces); an artwork face uses
     the artwork. When faces land, home is set again so every boss face repaints. */
  const BOSS_FACES = {};
  let bossFacesAsked = '';
  function bossFaceOf(boss) {
    if (!boss) return null;
    if (boss.poster_character_id) return { id: String(boss.poster_character_id), art: null };
    const lv = ctx.levelDef(boss.ends_level | 0), rows = lv && Array.isArray(lv.rows) ? lv.rows : [], f = rows.length ? rows[rows.length - 1].face : null;
    if (!f) return null;
    if (typeof f === 'string') return { id: f, art: null };
    return { id: String(f.character_id || f.id || ''), art: f.image === 'artwork' && f.artwork_id ? String(f.artwork_id) : null };
  }
  function loadBossFaces() {
    const bosses = Array.isArray(ctx.catalog().bosses) ? ctx.catalog().bosses : [];
    const served = new Set((Array.isArray(ctx.home().bosses) ? ctx.home().bosses : []).filter(x => x && x.poster && (x.poster.image_url || x.poster.image_thumb_url)).map(x => x.id));
    const want = bosses.filter(b => b && b.id && !served.has(b.id) && !BOSS_FACES[b.id]).map(b => ({ b: b, f: bossFaceOf(b) }))
      .filter(x => x.f && /^[0-9a-f-]{36}$/i.test(x.f.id)).slice(0, 60);
    const key = want.map(x => x.b.id).join(',');
    if (!want.length || key === bossFacesAsked) return;
    bossFacesAsked = key;
    const base = String(cfg.supabaseUrl || '').replace(/\/+$/, '');
    api.hallFaces(want.map(x => x.f.id)).then(r => {
      const by = {};
      for (const x of (r && r.heroes) || []) if (x && x.character_id) by[x.character_id] = x;
      let n = 0;
      for (const w of want) {
        const x = by[w.f.id], art = w.f.art && base ? base + '/storage/v1/object/public/hero-artwork/' + w.f.id + '/' + w.f.art + '/card.webp' : '';
        if (!x && !art) continue;
        BOSS_FACES[w.b.id] = { character_id: w.f.id, name: x ? x.name : '', image_url: art || x.img, image_thumb_url: art || x.thumb || x.img,
          image_offset_x: art ? 50 : x.frame.x, image_offset_y: art ? 50 : x.frame.y, image_zoom: art ? 100 : x.frame.zoom, fallback: true };
        n++;
      }
      if (n && store.get().home) store.set({ home: Object.assign({}, store.get().home) });
    }, () => { bossFacesAsked = ''; });
  }
  /* R800: named Hall heroes by character id for any screen that shows the story's cast (a chapter page's villains):
     ctx.hallFace(id) answers what has landed, ctx.wantFaces(ids) asks for the rest in one read and repaints once they
     arrive (home is set again, so the screen draws them) */
  const FACES = {}, FACES_ASKED = new Set();
  function wantFaces(ids) {
    const list = Array.from(new Set((Array.isArray(ids) ? ids : []).map(x => String(x || '').toLowerCase())))
      .filter(id => /^[0-9a-f-]{36}$/.test(id) && !FACES[id] && !FACES_ASKED.has(id)).slice(0, 60);
    if (!list.length) return;
    list.forEach(id => FACES_ASKED.add(id));
    api.hallFaces(list).then(r => {
      let n = 0;
      for (const x of (r && r.heroes) || []) if (x && x.character_id && !FACES[x.character_id]) { FACES[x.character_id] = x; n++; }
      if (n && store.get().home) store.set({ home: Object.assign({}, store.get().home) });
    }, () => { list.forEach(id => FACES_ASKED.delete(id)); });
  }

  /* ---------------- the shared context ---------------- */
  /* 2026-09-28 (stability S3): a lazy screen or overlay whose file did not arrive (a phone dropping one request) stays
     failed for the life of the page, so the same import never succeeds again. One more try under a fresh address (the
     import map's own URL with a retry mark); a second miss says so instead of leaving a dead button */
  const LOADS = {};
  function lazy(name, quiet) {
    if (LOADS[name]) return LOADS[name];
    const p = import(name).catch(err => {
      let url = '';
      try { url = JSON.parse(document.querySelector('script[type="importmap"]').textContent).imports[name] || ''; } catch (_) {}
      if (!url) throw err;
      return import(url + (url.indexOf('?') >= 0 ? '&' : '?') + 'retry=' + Date.now());
    }).catch(err => {
      delete LOADS[name];
      if (!quiet) toast('This part of the game could not load. Check your connection, then reload the page.', 'error');
      throw err;
    });
    LOADS[name] = p;
    return p;
  }
  /* an action's module: null when it could not load (the toast has spoken), so a tap never leaves a rejection behind */
  const use = name => lazy(name).catch(() => null);
  const ctx = {
    store: store, api: api, root: root, hallUrl: HALL,
    get router() { return router; },
    get shell() { return shell; },
    state: () => store.get(),
    home: () => store.get().home || {},
    catalog: () => store.get().catalog || {},
    online: () => !!store.get().net.online,
    go(path, opts) { if (router) router.go(path, opts); },
    tab(id) { if (router) router.tab(id); },
    levelDef(n) { const c = ctx.catalog(); return (Array.isArray(c.levels) ? c.levels : []).find(l => (l.level | 0) === (n | 0)) || null; },
    levelState(n) { const hm = ctx.home(); return (Array.isArray(hm.levels) ? hm.levels : []).find(l => (l.level | 0) === (n | 0)) || null; },
    bossDef(n) {
      const c = ctx.catalog(), lv = ctx.levelDef(n);
      const bosses = Array.isArray(c.bosses) ? c.bosses : [];
      return bosses.find(b => lv && b.id === lv.boss) || bosses.find(b => (b.ends_level | 0) === (n | 0)) || null;
    },
    packDef(id) { const p = ctx.catalog().packs; return p && Array.isArray(p.packs) ? p.packs.find(x => x.id === id) || null : null; },
    /* a level boss's live poster (home.bosses[].poster, §6.5.1), read from home and never from the
       cached catalog, so a poster Jan sets shows on the next home read. R801: a boss the server's poster list
       does not name (the campaign's new ids, boss_horn_captain and the rest) wears its own cast face from the
       Hall with the Hall's framing (bossFaces below); null shows the emblem */
    bossPoster(bossId) {
      const list = Array.isArray(ctx.home().bosses) ? ctx.home().bosses : [];
      const b = list.find(x => x && x.id === bossId);
      const p = b && b.poster;
      if (p && typeof p === 'object' && (p.image_url || p.image_thumb_url)) return p;
      return BOSS_FACES[bossId] || null;
    },
    hallFace(id) { return FACES[String(id || '').toLowerCase()] || null; },
    wantFaces: wantFaces,
    /* a write reply's own slices of home (daily_gift, bosses) are the server's truth: show them */
    patchHome(patch) {
      const hm = store.get().home;
      if (!hm || !patch || typeof patch !== 'object') return;
      store.set({ home: Object.assign({}, hm, patch) });
    },
    /* the player's star on a card (hoh_bgw_card_favorite): the reply's card replaces ours */
    async favorite(key, on) {
      const r = await api.favoriteCard(key, !!on);
      const had = ctx.cards.get(key);
      const card = r.card && r.card.card_key ? r.card : (had ? Object.assign({}, had, { favorite: !!r.favorite }) : null);
      if (card) ctx.cards.put(card);
      /* the heroes dot never counts a favorite as fodder: read home again once the star has landed */
      setTimeout(() => { ctx.refreshHome(); }, 700);
      return r;
    },
    /* three FramedFantasy portraits for the Mythic Pack and three everyday heroes for the Common
       Pack, from the Hall grid's own public read; null until they arrive (the fan shows its backs) */
    packFaces() {
      if (faces.value) return faces.value;
      const saved = readFaces();
      if (saved) { faces.value = saved; return saved; }
      if (!faces.loading && !faces.failed) {
        /* claimed before the first request: a request repaints this screen, which asks again */
        faces.loading = Promise.resolve().then(() => Promise.all([api.hallHeroes({ ff: true, limit: 40 }), api.hallHeroes({ ff: false, limit: 60 })])).then(pair => {
          const v = { v: 1, mythic: pickFew(pair[0].heroes, 3), common: pickFew(pair[1].heroes, 3) };
          faces.value = v; faces.loading = null;
          try { sessionStorage.setItem(FACES_KEY, JSON.stringify(v)); } catch (_) {}
          /* the pack fans are in the Shop (one tab since 2026-09-26) */
          ctx.rerender('shop');
          return v;
        }, () => { faces.loading = null; faces.failed = true; });
      }
      return null;
    },
    /* the level the player plays next: home's `next`, else the highest open one */
    currentLevel() {
      const lv = Array.isArray(ctx.home().levels) ? ctx.home().levels : [];
      const next = lv.find(l => l.next);
      if (next) return next.level | 0;
      const enabled = lv.filter(l => l.open && (ctx.levelDef(l.level) || {}).enabled !== false);
      return enabled.length ? enabled[enabled.length - 1].level | 0 : 1;
    },
    closed() { const hm = ctx.home(); return hm.open === false && hm.admin !== true; },
    /* Endless (Unlimited): home.unlimited.locked is the server's answer (R800: open after the
       Prologue, on the server's word). An older home without it falls back to the catalog's own
       unlock level; no level is written here. */
    unlimitedOpen() {
      const u = ctx.home().unlimited;
      if (u && typeof u.locked === 'boolean') return !u.locked;
      const need = ((ctx.catalog().unlimited || {}).unlock_after_level | 0);
      if (!need) return true;
      const l = ctx.levelState(need);
      return !!(l && l.cleared);
    },
    /* the lock line and the level it names; null when Endless is open. The level is always the
       server's (home.unlimited.needs_level): the line names it as a chapter ("Clear the Prologue to
       open Endless."), so the R796 server's words ("Clear Level 5 to unlock Unlimited") never show
       the old name. Its own message is read only when it names no level. */
    unlimitedLock() {
      if (ctx.unlimitedOpen()) return null;
      const u = ctx.home().unlimited || {};
      const need = (u.needs_level | 0) || ((ctx.catalog().unlimited || {}).unlock_after_level | 0) || 0;
      const said = String(u.message || '').trim();
      return { needs: need, message: need ? 'Clear ' + chapterOf(need).ref + ' to open Endless.' : said ? (/[.!?]$/.test(said) ? said : said + '.') : 'Endless is not open yet.' };
    },
    /* a live Endless run (no level) may always be finished, locked or not */
    endlessLive() { const r = ctx.home().live_run; return !!(r && r.level == null); },
    line(err, context) { return playerLine(err, context || 'generic'); },
    kind(err) { return errorKind(err); },
    rerender(view) { if (shell) shell.rerender(view); },
    toast: toast,
    sheet: sheet,
    async refreshHome() {
      try {
        const home = await api.home();
        store.set({ home: home });
        return home;
      } catch (e) { return null; }
    },
    /* a write reply that carries the wallet is the server's truth: show it */
    takeWallet(wallet) {
      if (!wallet || typeof wallet !== 'object') return;
      const hm = store.get().home;
      if (!hm) return;
      const w = Object.assign({}, hm.wallet || {});
      if (wallet.gold != null) w.gold = wallet.gold;
      if (wallet.crystals != null) w.crystals = wallet.crystals;
      store.set({ home: Object.assign({}, hm, { wallet: w }) });
      if (shell) shell.bump(Object.keys(wallet));
    },
    /* ---- cards: every page of hoh_bgw_cards_list, kept by key ---- */
    cards: {
      get: key => (store.get().cards.byKey || {})[key] || null,
      all: () => { const c = store.get().cards; return (c.order || []).map(k => c.byKey[k]).filter(Boolean); },
      state: () => store.get().cards,
      put(card) {
        if (!card || !card.card_key) return;
        const c = store.get().cards;
        const byKey = Object.assign({}, c.byKey, { [card.card_key]: card });
        const order = c.order.indexOf(card.card_key) >= 0 ? c.order : c.order.concat([card.card_key]);
        store.set({ cards: Object.assign({}, c, { byKey: byKey, order: order }) });
      },
      drop(key) {
        const c = store.get().cards;
        if (!c.byKey[key]) return;
        const byKey = Object.assign({}, c.byKey); delete byKey[key];
        store.set({ cards: Object.assign({}, c, { byKey: byKey, order: c.order.filter(k => k !== key) }) });
      },
      invalidate() { cardsSeq++; store.set({ cards: { byKey: {}, order: [], next: null, loaded: false, loading: false, error: null } }); },
      loadAll(force) {
        const c = store.get().cards;
        if (!force && (c.loaded || c.loading)) return c.loading ? ctx.cards._p : Promise.resolve(ctx.cards.all());
        const seq = ++cardsSeq;
        store.set({ cards: Object.assign({ byKey: {}, order: [], next: null }, force ? {} : c, { loading: true, error: null }) });
        const byKey = {}, order = [];
        const page = (after, n) => api.cards({ after: after, limit: 120 }).then(r => {
          if (seq !== cardsSeq) return null;
          for (const card of (r.cards || [])) { if (!byKey[card.card_key]) order.push(card.card_key); byKey[card.card_key] = card; }
          if (r.next && n < 40) return page(r.next, n + 1);
          return null;
        });
        ctx.cards._p = page(null, 0).then(() => {
          if (seq !== cardsSeq) return ctx.cards.all();
          store.set({ cards: { byKey: byKey, order: order, next: null, loaded: true, loading: false, error: null } });
          return ctx.cards.all();
        }, err => {
          if (seq === cardsSeq) store.set({ cards: Object.assign({}, store.get().cards, { loading: false, error: playerLine(err, 'generic') }) });
          throw err;
        });
        return ctx.cards._p;
      }
    },
    /* lazily loaded screens and overlays */
    load(name, quiet) { return lazy(name, quiet); },
    async openPack(reply, packId, opts) { const m = await use('bg-pack'); return m && m.reveal(ctx, reply, packId, opts); },
    async showResult(reply, info) { const m = await use('bg-result'); return m && m.show(ctx, reply, info); },
    /* R800: the guest Prologue for a visitor with no Hall account (bg-battle guest), and its ending */
    async guest(loaner, button) { const m = await use('bg-battle'); return m && m.guest(ctx, loaner, button); },
    guestEnd(result) { showGuestEnd(result || {}); },
    /* R800 (§11): the Prologue with a loaner Champion; its boss card plays first on a first meeting too */
    async prologue(loaner, button) {
      if (fightGate) return;
      fightGate = true;
      try {
        const boss = ctx.bossDef(1);
        if (boss && boss.id && !bossSeen(boss.id) && ctx.bossPoster(boss.id)) { markBossSeen(boss.id); try { await ctx.cutscene.bossCard(boss.id); } catch (_) {} }
        const m = await use('bg-battle'); return m ? await m.prologue(ctx, loaner, button) : undefined;
      } finally { fightGate = false; }
    },
    /* R800: the first time a chapter's boss is met on this device, its intro card plays before the fight.
       R818 M7: a chapter's first entry plays its opener instead (and Ember's start line), once the server sends Chapter
       III (bg-story openerDue answers null before that, so nothing changes until then) */
    async fight(level, button) {
      if (fightGate) return;
      fightGate = true;
      try {
        const boss = ctx.bossDef(level);
        let opened = false;
        if (!ctx.closed() && openerDue(ctx, level)) {
          const cs = await lazy('bg-cutscene', true).catch(() => null);
          try { opened = !!(cs && await cs.opener(ctx, level)); } catch (_) {}
        }
        if (opened) { if (boss && boss.id) markBossSeen(boss.id); }
        else if (boss && boss.id && !bossSeen(boss.id) && ctx.bossPoster(boss.id) && !ctx.closed()) {
          markBossSeen(boss.id);
          try { await ctx.cutscene.bossCard(boss.id); } catch (_) {}
        }
        const m = await use('bg-battle'); return m ? await m.fight(ctx, level, button) : undefined;
      } finally { fightGate = false; }
    },
    cutscene: {
      async intro() { const m = await use('bg-cutscene'); return m && m.play(ctx, 'intro_hush'); },
      async bossCard(bossId, opts) { if (!bossId) return null; const m = await use('bg-cutscene'); return m && m.bossCard(ctx, bossId, opts); },
      async oranPhase(opts) { const m = await use('bg-cutscene'); return m && m.oranPhase(ctx, opts); },
      async play(id, opts) { const m = await use('bg-cutscene'); return m && m.play(ctx, id, opts); },
      /* R818 M7: story playback (bg-cutscene story: 'stop', 'result', 'oran'); quiet when its file does not load, so a
         stop or a result always goes on */
      async story(moment, opts) { const m = await lazy('bg-cutscene', true).catch(() => null); return m && typeof m.story === 'function' ? m.story(ctx, moment, opts) : null; }
    },
    async resume(button) { const m = await use('bg-battle'); return m && m.resume(ctx, button); },
    async unlimited(button) { const m = await use('bg-battle'); return m && m.unlimited(ctx, button); },
    /* the Codex sheet; codex('Burn') opens it searched for that term (window.HohCodex's id, term or alias) */
    async codex(term) { const m = await use('bg-events'); return m && m.codex(ctx, term); },
    async history() { const m = await use('bg-events'); return m && m.history(ctx); }
  };

  try {
    Object.defineProperty(window, 'HOHBattlegrounds', {
      configurable: true,
      value: Object.freeze({ version: 'R796', store: store, api: api, ctx: ctx, get router() { return router; } })
    });
  } catch (_) {}

  function setState(s) {
    root.setAttribute('data-bg-state', s);
    patchSlice(store, 'ui', { boot: s });
  }
  function clearStage() {
    for (const n of Array.from(root.querySelectorAll('[data-bg-stage]'))) n.remove();
  }
  function stage(node) {
    clearStage();
    if (bootPanel) bootPanel.hidden = true;
    node.setAttribute('data-bg-stage', '');
    node.classList.add('bg-a-fade');
    root.appendChild(node);
    return node;
  }
  function dropShell() {
    if (router) { router.stop(); router = null; }
    if (shell) { shell.destroy(); shell = null; }
  }

  /* ---- bring your hero to life ---- */
  function showBring(reason) {
    dropShell();
    setState(reason === 'no_hero' ? 'no_hero' : 'signed_out');
    const signedOut = reason !== 'no_hero';
    const guest = signedOut && guestOn() ? guestOffer() : null;
    stage(h('section', { class: 'bg-card-state bg-bring' + (guest ? ' bg-bring--guest' : ''), 'data-bg-reason': reason, 'aria-labelledby': 'bg-bring-h' },
      h('div', { class: 'bg-card-state__glow', 'aria-hidden': 'true' }),
      h('div', { class: 'bg-card-state__art', 'aria-hidden': 'true' }, h('span', { class: 'bg-side__box', style: { width: px(64), height: px(64), borderRadius: px(16), color: '#E6C88C' } }, icon('heroes', 30))),
      h('h1', { id: 'bg-bring-h', class: 'bg-h1', text: 'Bring your hero to life' }),
      h('p', { class: 'bg-line', text: 'Your hero from the Hall of Heroes fights here.' }),
      signedOut ? h('p', { class: 'bg-line bg-dim', text: 'Already added one? Sign in from the Hall first.' }) : null,
      h('a', { class: 'bg-btn bg-btn--primary bg-btn--lg', href: signedOut ? HALL : HALL + '#add-hero', 'data-bg-action': 'add-hero', text: 'Add a Hero' }),
      guest
    ));
  }

  /* ---- R800: the guest Prologue, for a visitor with no Hall account ----
     On when the page says so (HOH_BG_CONFIG.guestPrologue, set once the engine's guestLevel and the overlay's
     playGuestLevel ship) or for a check with ?guest=1. Three everyday Hall heroes to borrow (bg-data guestLoaners);
     a pick plays the Prologue in the page (bg-battle guest), nothing is saved, and it ends on "Bring your own hero
     to keep going". */
  function guestOn() {
    if (cfg.guestPrologue === true) return true;
    try { return new URLSearchParams(location.search).get('guest') === '1'; } catch (_) { return false; }
  }
  const firstOf = name => firstName(name, 'this hero');
  /* R802: a phone shows the three side by side, so the offer fits the screen without scrolling ("Play", its hero
     named on the card and in the button's label) */
  function guestCard(l) {
    const btn = h('button', { type: 'button', class: 'bg-btn bg-btn--block', 'data-fk': 'guest-' + l.character_id, 'data-bg-action': 'guest-pick', 'aria-label': 'Play as ' + firstOf(l.name) },
      h('span', { text: 'Play' }), h('span', { class: 'bg-guest__who', text: ' as ' + firstOf(l.name) }));
    btn.addEventListener('click', () => { ctx.guest(l, btn); });
    return h('article', { class: 'bg-card bg-loaner', 'data-bg-guest-loaner': l.character_id },
      portrait({ img: l.img, name: l.name, frame: l.frame, size: 132, radius: 12, alt: l.name }),
      h('h3', { class: 'bg-loaner__name', text: l.name }),
      h('p', { class: 'bg-loaner__meta', text: l.class_text || l.cls || 'Hero' }),
      btn);
  }
  function guestOffer() {
    const row = h('div', { class: 'bg-loaners__row', 'aria-busy': 'true' }, [0, 1, 2].map(() => h('div', { class: 'bg-card bg-loaner bg-loaner--skel', 'aria-hidden': 'true' }, h('span', { class: 'bg-skel bg-loaner__skel' }))));
    const block = h('div', { class: 'bg-guest', 'data-bg-guest': '', role: 'group', 'aria-labelledby': 'bg-guest-h' },
      h('h2', { id: 'bg-guest-h', class: 'bg-guest__h', text: 'Or try the Prologue first' }),
      h('p', { class: 'bg-line', text: 'Borrow a Hall hero. No account needed.' }),
      row);
    api.guestLoaners().then(r => {
      const list = (r && Array.isArray(r.loaners) ? r.loaners : []).slice(0, 3);
      if (!list.length) { block.remove(); return; }
      row.removeAttribute('aria-busy');
      row.replaceChildren(...list.map(guestCard));
    }, () => block.remove());
    return block;
  }
  /* the guest Prologue's end: cleared, lost or left, and the one next step (your own hero) */
  function showGuestEnd(r) {
    dropShell();
    setState('guest_end');
    const l = r.loaner || {}, first = firstOf(l.name), cleared = !!r.cleared, lost = !cleared && !r.left;
    const again = h('button', { type: 'button', class: 'bg-btn ' + (lost ? 'bg-btn--primary bg-btn--lg' : 'bg-btn--quiet'), 'data-bg-action': 'guest-again',
      text: lost ? 'Play again with ' + first : 'Play the Prologue again' });
    again.addEventListener('click', () => { ctx.guest(l, again); });
    const add = h('a', { class: 'bg-btn ' + (lost ? 'bg-btn--lg' : 'bg-btn--primary bg-btn--lg'), href: HALL + '#add-hero', 'data-bg-action': 'add-hero', text: 'Add a Hero' });
    stage(h('section', { class: 'bg-card-state bg-guest-end', 'data-bg-reason': cleared ? 'guest-cleared' : lost ? 'guest-lost' : 'guest-left', 'aria-labelledby': 'bg-guest-end-h' },
      h('div', { class: 'bg-card-state__glow', 'aria-hidden': 'true' }),
      l.img ? h('div', { class: 'bg-card-state__art', 'aria-hidden': 'true' }, portrait({ img: l.img, name: l.name, frame: l.frame, size: 112, radius: 14, eager: true, alt: '' })) : null,
      h('h1', { id: 'bg-guest-end-h', class: 'bg-h1', text: cleared ? 'You cleared the Prologue' : lost ? 'Not this time' : 'The Prologue is waiting' }),
      h('p', { class: 'bg-line', text: cleared ? 'Add your own hero to keep going.'
        : lost ? first + ' fought hard. Try again, or add your own hero.'
        : 'Add your own hero, or try again with a borrowed one.' }),
      l.name ? h('p', { class: 'bg-line bg-dim', text: 'You played as ' + first + '. Nothing from this run is kept.' }) : null,
      lost ? again : add,
      lost ? add : again));
  }

  /* ---- R800 onboarding: choose a loaner Champion for the Prologue (§11) ----
     Three approved Hall heroes the server offers (home.prologue.loaners, docs/R801-ENGINE117-PLAN.md
     §6.1), each named and credited to its owner, for an account whose own hero is not in the Hall yet
     (waiting for approval, or hidden). A pick starts the Prologue with that hero at once
     (hoh_bgw_prologue_start) in the battle overlay; your own hero stays one tap away. */
  function showLoaners(list, home) {
    dropShell();
    setState('loaners');
    const cleared = !!((home && Array.isArray(home.levels) ? home.levels : []).find(l => (l.level | 0) === 1 && l.cleared));
    const cls = c => { const t = String(c || '').trim(); return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Hero'; };
    const cards = list.slice(0, 3).map(l => {
      const img = l.img || l.image_url || l.thumb || l.image_thumb_url || '';
      const first = firstName(l.name, 'this hero');
      const btn = h('button', { type: 'button', class: 'bg-btn ' + (cleared ? '' : 'bg-btn--primary ') + 'bg-btn--block', 'data-fk': 'loaner-' + l.character_id, 'data-bg-action': 'loaner-pick', text: 'Play as ' + first });
      /* the pick starts the Prologue at once with that hero (hoh_bgw_prologue_start), in the battle overlay */
      btn.addEventListener('click', () => { ctx.prologue(l, btn); });
      return h('article', { class: 'bg-card bg-loaner', 'data-bg-loaner': l.character_id },
        img ? portrait({ img: img, name: l.name, size: 132, radius: 12, eager: true, alt: l.name }) : h('span', { class: 'bg-side__box', style: { width: px(132), height: px(132) } }, icon('heroes', 30)),
        h('h2', { class: 'bg-loaner__name', text: l.name || 'A Hall hero' }),
        h('p', { class: 'bg-loaner__meta', text: cls(l.cls || l.class) + ((l.owner || l.owner_name) ? ', a hero of ' + (l.owner || l.owner_name) : '') }),
        btn);
    });
    /* once the Prologue is cleared, the one next step is your own hero (the loaners stay, to play it again) */
    stage(h('section', { class: 'bg-card-state bg-loaners' + (cleared ? ' bg-loaners--cleared' : ''), 'data-bg-reason': cleared ? 'loaners-cleared' : 'loaners', 'aria-labelledby': 'bg-loaners-h' },
      h('div', { class: 'bg-card-state__glow', 'aria-hidden': 'true' }),
      h('h1', { id: 'bg-loaners-h', class: 'bg-h1', text: cleared ? 'You cleared the Prologue' : 'Choose a Champion for the Prologue' }),
      h('p', { class: 'bg-line', text: cleared ? 'Add your own hero to play Chapter I.' : 'Borrow a Hall hero for your first five rounds.' }),
      cleared ? h('a', { class: 'bg-btn bg-btn--primary bg-btn--lg', href: HALL + '#add-hero', 'data-bg-action': 'add-hero', text: 'Add your hero' }) : null,
      cleared ? h('p', { class: 'bg-loaners__again', text: 'Or play the Prologue again with a loaner.' }) : null,
      h('div', { class: 'bg-loaners__row' }, cards),
      cleared ? null : h('a', { class: 'bg-btn bg-btn--quiet', href: HALL + '#add-hero', 'data-bg-action': 'add-hero', text: 'Add your own hero in the Hall' })));
  }

  /* ---- error with Try again ---- */
  function showError(err) {
    const info = errorKind(err);
    if (info.kind === 'account') { showBring('signed_out'); return; }
    dropShell();
    setState('error');
    stage(h('section', { class: 'bg-card-state bg-error', role: 'alert', 'data-bg-kind': info.kind, 'aria-labelledby': 'bg-error-h' },
      h('div', { class: 'bg-card-state__art', 'aria-hidden': 'true' }, h('span', { class: 'bg-side__box', style: { width: px(64), height: px(64), borderRadius: px(16), color: '#9AA3B2' } }, icon('retry', 28))),
      h('h1', { id: 'bg-error-h', class: 'bg-h1', text: 'The Battlegrounds did not open' }),
      h('p', { class: 'bg-line', text: playerLine(err, 'home') }),
      h('button', { type: 'button', class: 'bg-btn bg-btn--primary bg-btn--lg', 'data-bg-action': 'retry', text: 'Try again', on: { click: () => load() } }),
      h('a', { class: 'bg-btn bg-btn--quiet', href: HALL, text: 'Back to the Hall' })
    ));
  }

  /* ---- online and offline ---- */
  window.addEventListener('offline', () => patchSlice(store, 'net', { online: false }));
  window.addEventListener('online', () => {
    patchSlice(store, 'net', { online: true });
    if (!shell) return;
    api.home().then(home => { if (shell) store.set({ home: home }); }, () => {});
  });

  /* ---- load ---- */
  async function load() {
    const seq = ++loadSeq;
    const session = api.session();
    store.set({ session: session });
    if (!session.signedIn) { showBring('signed_out'); return; }
    dropShell();
    clearStage();
    if (bootPanel) bootPanel.hidden = false;
    if (bootLine) bootLine.textContent = 'Opening the Battlegrounds';
    setState('loading');
    try {
      const pair = await bound(Promise.all([api.catalog(), api.home()]), BOOT_BOUND_MS);
      if (seq !== loadSeq) return;
      let catalog = pair[0];
      const home = pair[1];
      if (home.content_sha && catalog.content_sha !== home.content_sha) {
        catalog = await bound(api.catalog({ sha: home.content_sha }), BOOT_BOUND_MS);
        if (seq !== loadSeq) return;
      }
      store.set({ catalog: catalog, home: home });
      loadBossFaces();
      if (home.account && home.account.has_hero === false) {
        /* R800 (§11): a server that offers loaner Champions (home.prologue) lets an account whose hero is not in
           the Hall yet play the Prologue; otherwise the R796 path */
        const pro = home.prologue;
        if (pro && pro.v === 1 && pro.loaner === true && Array.isArray(pro.loaners) && pro.loaners.length) showLoaners(pro.loaners, home); else showBring('no_hero');
        return;
      }
      /* on a level's own page the boss poster is the largest paint: start it now, not after the fonts. On the
         Hub (R800) the largest paint is the Champion's portrait, and the poster is a small face that must not
         take the bandwidth first */
      if (/^#\/level\/\d+$/.test(location.hash)) try {
        const boss = ctx.bossDef(((/\d+$/.exec(location.hash) || [])[0] | 0) || ctx.currentLevel());
        const src = posterSrc(boss ? ctx.bossPoster(boss.id) : null);
        if (src) { const im = new Image(); im.decoding = 'async'; try { im.fetchPriority = 'high'; } catch (_) {} im.src = src; }
      } catch (_) {}
      /* the cards list is not needed for the first frame: it fills in portraits */
      if (!ctx.closed()) ctx.cards.loadAll().catch(() => {});
      await fontsGate;
      if (titleUp) await Promise.race([titleUp, sleep(1500)]);
      if (seq !== loadSeq) return;
      clearStage();
      if (bootPanel) bootPanel.hidden = true;
      shell = createShell(ctx);
      root.appendChild(shell.el);
      router = createRouter({ store: store, root: shell.el });
      shell.attachRouter(router);
      router.start();
      setState('home');
      /* 2026-09-28: the embers and the breathing red dots (bg-ambient), after the first screen so they never
         compete with it; a reload's new shell takes the same layer */
      afterFirstScreen(root, () => { lazy('bg-ambient', true).then(m => { if (shell) m.start(ctx, shell.el); }).catch(() => {}); });
      /* R812: the class paths (one read a page) once the first screen is up, so the Party and the battle's path labels
         have them at once. Before R812 home has no classes and nothing is asked. */
      if (home.classes && typeof api.classPaths === 'function') afterFirstScreen(root, () => { api.classPaths().catch(() => {}); });
      if (!vendorAsked) {
        vendorAsked = true;
        afterFirstScreen(root, () => {
          if (!ctx.closed()) ctx.packFaces();
          for (const m of LAZY) lazy(m, true).catch(() => {});
          /* PERF item 6: the battle's scripts download in the background a little after, so Fight only compiles them */
          setTimeout(() => { if (!ctx.closed()) lazy('bg-battle', true).then(m => { if (m && typeof m.warmStack === 'function') m.warmStack(); }).catch(() => {}); }, 2500);
        });
      }
    } catch (err) {
      if (seq === loadSeq) showError(err);
    }
  }
  ctx.reload = load;

  setState('boot');
  /* R800: the first visit opens on the title and its intro while the Hub loads underneath */
  titleUp = introSeen() ? null : lazy('bg-cutscene', true).then(m => { m.title(ctx); }).catch(() => {});
  load();
}

boot();
